import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Receipt, Trash, Check, DownloadSimple, UploadSimple, CaretLeft, CaretRight, FilePdf, PaperPlaneTilt, CreditCard, Bank, Share, WhatsappLogo, Copy, EnvelopeSimple } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast, Toaster } from "sonner";

const EMPTY = { package_id: "", customer_name: "", customer_email: "", amount: "", currency: "JMD", due_date: "", notes: "" };

export default function Invoices() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [packages, setPackages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const importRef = React.useRef(null);

  const readOnly = user.role === "user" || user.role === "customer" || user.role === "super_admin";
  const canPayOwn = user.role === "customer"; // Customer can pay their own invoices (filtered by API)
  // Both super_admin and company_admin can record an offline (bank transfer) payment.
  const canMarkOffline = user.role === "super_admin" || user.role === "company_admin";

  // Offline-payment dialog state
  const [offlineInv, setOfflineInv] = useState(null);
  const [offlineMethod, setOfflineMethod] = useState("bank_transfer");
  const [offlineRef, setOfflineRef] = useState("");
  const [offlineNote, setOfflineNote] = useState("");
  const [offlineSaving, setOfflineSaving] = useState(false);

  const load = async () => {
    const params = { page, page_size: pageSize };
    if (filter !== "all") params.status = filter;
    const { data } = await api.get("/invoices", { params });
    setRows(data.items || []);
    setTotal(data.total || 0);
    if (!readOnly && packages.length === 0) {
      const p = await api.get("/packages", { params: { page_size: 100 } });
      setPackages(p.data.items || []);
    }
    if (user.role === "super_admin" && companies.length === 0) {
      const c = await api.get("/companies");
      setCompanies(c.data);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, page]);

  const save = async () => {
    try {
      const payload = { ...form, amount: parseFloat(form.amount || 0) };
      if (!payload.package_id) delete payload.package_id;
      if (!payload.due_date) delete payload.due_date;
      if (!payload.notes) delete payload.notes;
      if (!payload.customer_email) delete payload.customer_email;
      await api.post("/invoices", payload);
      toast.success("Invoice created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const markPaid = async (iv) => {
    try {
      await api.patch(`/invoices/${iv.id}`, { status: "paid" });
      toast.success("Invoice marked as paid");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  // Build the public, unguessable pay link for an invoice. Always uses the
  // CURRENT origin so dispatcher previews from preview.kanal.com don't accidentally
  // share a production link (and vice-versa).
  const buildPayUrl = (iv) => `${window.location.origin}/pay/${iv.id}`;

  const copyPayLink = async (iv) => {
    const url = buildPayUrl(iv);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Payment link copied — paste anywhere.");
    } catch {
      // Clipboard API can fail on insecure context / older browsers — fallback
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Payment link copied."); }
      catch { toast.error("Couldn't access clipboard"); }
      document.body.removeChild(ta);
    }
  };

  const shareWhatsApp = (iv) => {
    const url = buildPayUrl(iv);
    const cur = iv.currency || "JMD";
    const msg = `Hi ${iv.customer_name || "there"}, your invoice ${iv.invoice_number} (${cur} $${Number(iv.amount).toLocaleString()}) is ready to pay. Tap here: ${url}`;
    const phone = (iv.customer_phone || "").replace(/[^\d+]/g, "");
    const waUrl = phone
      ? `https://wa.me/${phone.replace(/^\+/, "")}?text=${encodeURIComponent(msg)}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  const shareEmail = (iv) => {
    const url = buildPayUrl(iv);
    const cur = iv.currency || "JMD";
    const subject = `Invoice ${iv.invoice_number} — payment link`;
    const body = `Hi ${iv.customer_name || "there"},\n\nYour invoice ${iv.invoice_number} for ${cur} $${Number(iv.amount).toLocaleString()} is ready.\n\nTap to pay securely (no login required):\n${url}\n\nThank you,\nKanal`;
    const to = iv.customer_email ? encodeURIComponent(iv.customer_email) : "";
    window.location.href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // Submit the offline (bank transfer / cash / cheque) payment dialog
  const submitOfflinePay = async () => {
    if (!offlineInv) return;
    setOfflineSaving(true);
    try {
      await api.post(`/invoices/${offlineInv.id}/mark-paid-offline`, {
        payment_method: offlineMethod,
        payment_reference: offlineRef.trim() || null,
        note: offlineNote.trim() || null,
      });
      toast.success(`Invoice ${offlineInv.invoice_number} marked paid (${offlineMethod.replace("_", " ")})`);
      setOfflineInv(null);
      setOfflineRef("");
      setOfflineNote("");
      setOfflineMethod("bank_transfer");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to mark paid");
    } finally { setOfflineSaving(false); }
  };

  const viewPdf = async (iv) => {
    try {
      const resp = await api.get(`/invoices/${iv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error("Failed to load PDF");
    }
  };

  const sendInvoice = async (iv) => {
    if (!confirm(`Send invoice ${iv.invoice_number} to ${iv.customer_email || 'company contact email'}?`)) return;
    try {
      const { data } = await api.post(`/invoices/${iv.id}/send`);
      toast.success(`Invoice sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const payInvoice = async (iv) => {
    try {
      const { data } = await api.post(`/invoices/${iv.id}/wipay/checkout`);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("No payment URL returned");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment init failed");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this invoice?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const companyName = (id) => companies.find(c => c.id === id)?.name || "—";

  const exportCSV = async () => {
    try {
      const resp = await api.get("/invoices/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = "invoices.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };
  const importCSV = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/invoices/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} invoices${data.errors?.length ? ` (${data.errors.length} errors)` : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const total_amount = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const paid = rows.filter(r => r.status === "paid").reduce((s, r) => s + r.amount, 0);
  const unpaid = total_amount - paid;

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]" data-testid="invoices-page">
      <Toaster position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Billing</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mt-1" style={{ fontFamily: "Work Sans" }}>Invoices</h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {(user.role === "super_admin" || (user.plan_features && user.plan_features.csv_import_export)) && (
            <>
              <Button variant="outline" onClick={exportCSV} data-testid="export-invoices-btn">
                <DownloadSimple size={15} className="mr-1.5" /> Export CSV
              </Button>
              {!readOnly && user.role !== "super_admin" && (
                <>
                  <input type="file" accept=".csv" ref={importRef} className="hidden" onChange={(e) => e.target.files?.[0] && importCSV(e.target.files[0])} data-testid="import-invoices-input" />
                  <Button variant="outline" onClick={() => importRef.current?.click()} data-testid="import-invoices-btn">
                    <UploadSimple size={15} className="mr-1.5" /> Import
                  </Button>
                </>
              )}
            </>
          )}
          {!readOnly && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="add-invoice-btn">
                <Plus size={16} className="mr-1.5" weight="bold" /> New Invoice
              </Button>
            </DialogTrigger>
            <DialogContent
              className="w-[calc(100vw-2rem)] sm:w-full sm:max-w-lg p-0 max-h-[90vh] gap-0 overflow-hidden flex flex-col"
              data-testid="invoice-dialog"
            >
              <DialogHeader className="px-5 sm:px-6 pt-5 pb-3 border-b border-zinc-100 shrink-0">
                <DialogTitle>Create Invoice</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 overflow-y-auto px-5 sm:px-6 py-4 flex-1" data-testid="invoice-dialog-body">
                <Field label="Customer name" value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} testId="inv-customer" />
                <Field label="Customer email (optional)" value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} testId="inv-customer-email" />
                <div className="grid grid-cols-[1fr_auto] gap-3">
                  <Field label="Amount" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testId="inv-amount" />
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Currency</Label>
                    <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                      <SelectTrigger className="mt-1.5 w-24" data-testid="inv-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="JMD">JMD</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Field label="Due date" type="date" value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} testId="inv-due" />
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Link to package (optional)</Label>
                  <Select value={form.package_id} onValueChange={(v) => setForm({ ...form, package_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="inv-package"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      {packages.slice(0, 50).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.tracking_number} — {p.recipient_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Notes</Label>
                  <Textarea rows={2} className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="inv-notes" />
                </div>
              </div>
              <DialogFooter className="px-5 sm:px-6 py-4 border-t border-zinc-100 bg-white shrink-0 gap-2 sm:gap-0">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="save-invoice-btn">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Summary — financial KPIs visible only to admins */}
      {(user.role === "company_admin" || user.role === "super_admin") && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="invoice-summary">
          <SummaryCard label="Total" value={`$${total_amount.toLocaleString()}`} color="#09090B" />
          <SummaryCard label="Paid" value={`$${paid.toLocaleString()}`} color="#10B981" />
          <SummaryCard label="Outstanding" value={`$${unpaid.toLocaleString()}`} color="#F59E0B" />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="invoices-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-5 py-3 font-semibold">Invoice #</th>
              <th className="px-5 py-3 font-semibold">Customer</th>
              {user.role === "super_admin" && <th className="px-5 py-3 font-semibold">Company</th>}
              <th className="px-5 py-3 font-semibold">Amount</th>
              <th className="px-5 py-3 font-semibold">Due</th>
              <th className="px-5 py-3 font-semibold">Status</th>
              <th className="px-5 py-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-zinc-500">
                <Receipt size={32} className="mx-auto mb-2 text-zinc-300" />
                No invoices yet.
              </td></tr>
            ) : rows.map((iv) => (
              <tr key={iv.id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`inv-row-${iv.id}`}>
                <td className="px-5 py-4"><code className="text-xs font-mono text-zinc-900 bg-zinc-100 px-1.5 py-0.5 rounded">{iv.invoice_number}</code></td>
                <td className="px-5 py-4 font-medium text-zinc-900">{iv.customer_name}</td>
                {user.role === "super_admin" && <td className="px-5 py-4 text-zinc-700">{companyName(iv.company_id)}</td>}
                <td className="px-5 py-4 font-semibold text-zinc-900">{iv.currency || 'JMD'} ${iv.amount.toLocaleString()}</td>
                <td className="px-5 py-4 text-zinc-500 text-xs">{iv.due_date || "—"}</td>
                <td className="px-5 py-4"><StatusBadge status={iv.status} /></td>
                <td className="px-5 py-4 text-right">
                  <Button variant="ghost" size="sm" onClick={() => viewPdf(iv)} data-testid={`view-inv-${iv.id}`} title="View PDF">
                    <FilePdf size={15} />
                  </Button>
                  {!readOnly && (
                    <Button variant="ghost" size="sm" onClick={() => sendInvoice(iv)} data-testid={`send-inv-${iv.id}`} title="Send to customer">
                      <PaperPlaneTilt size={15} />
                    </Button>
                  )}
                  {(!readOnly || user.role === "super_admin") && (iv.status === "unpaid" || iv.status === "open") && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`share-inv-${iv.id}`} title="Share pay link" className="text-indigo-700 hover:bg-indigo-50">
                          <Share size={15} className="mr-1" /> Share
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-64 p-2" data-testid={`share-popover-${iv.id}`}>
                        <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
                          Share payment link
                        </div>
                        <button
                          type="button"
                          onClick={() => copyPayLink(iv)}
                          className="w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-zinc-50 text-sm transition-colors"
                          data-testid={`share-copy-${iv.id}`}
                        >
                          <Copy size={16} className="text-zinc-500" weight="bold" />
                          <div className="min-w-0">
                            <div className="font-medium text-zinc-900">Copy link</div>
                            <div className="text-[10px] text-zinc-500 truncate">/pay/{iv.id.slice(0, 8)}…</div>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => shareWhatsApp(iv)}
                          className="w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-emerald-50 text-sm transition-colors"
                          data-testid={`share-whatsapp-${iv.id}`}
                        >
                          <WhatsappLogo size={16} className="text-emerald-600" weight="fill" />
                          <div className="min-w-0">
                            <div className="font-medium text-zinc-900">WhatsApp</div>
                            <div className="text-[10px] text-zinc-500 truncate">{iv.customer_phone || "Choose a contact"}</div>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => shareEmail(iv)}
                          className="w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-blue-50 text-sm transition-colors"
                          data-testid={`share-email-${iv.id}`}
                        >
                          <EnvelopeSimple size={16} className="text-blue-600" weight="bold" />
                          <div className="min-w-0">
                            <div className="font-medium text-zinc-900">Email</div>
                            <div className="text-[10px] text-zinc-500 truncate">{iv.customer_email || "Opens your mail app"}</div>
                          </div>
                        </button>
                      </PopoverContent>
                    </Popover>
                  )}
                  {(!readOnly || canPayOwn) && (iv.status === "unpaid" || iv.status === "open") && (
                    <Button variant="ghost" size="sm" onClick={() => payInvoice(iv)} data-testid={`pay-invoice-${iv.id}`} className="text-[#002BF6] hover:bg-blue-50" title="Pay with WiPay">
                      <CreditCard size={15} className="mr-1" /> Pay
                    </Button>
                  )}
                  {(!readOnly || canPayOwn) && (iv.status === "unpaid" || iv.status === "open") && (
                    <a
                      href="https://jm.wipayfinancial.com/to_me/cyrusfieldtech"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        try {
                          navigator.clipboard?.writeText(iv.invoice_number);
                          toast.success(`Invoice # ${iv.invoice_number} copied — paste it into the WiPay reference field on the Cyrus Field page.`);
                        } catch { /* clipboard not available */ }
                      }}
                      data-testid={`pay-cyrusfield-${iv.id}`}
                      className="inline-flex items-center h-8 px-2.5 rounded text-xs font-medium text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-500/10"
                      title="Pay via Cyrus Field's WiPay link (opens a new tab — reference the invoice # in the WiPay note field)"
                    >
                      <CreditCard size={14} className="mr-1" /> Pay via Cyrus Field
                    </a>
                  )}
                  {!readOnly && (iv.status === "unpaid" || iv.status === "open") && (
                    <Button variant="ghost" size="sm" onClick={() => markPaid(iv)} data-testid={`mark-paid-${iv.id}`} className="text-emerald-700 hover:bg-emerald-50">
                      <Check size={15} className="mr-1" /> Mark paid
                    </Button>
                  )}
                  {canMarkOffline && (iv.status === "unpaid" || iv.status === "open") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setOfflineInv(iv); setOfflineRef(""); setOfflineNote(""); setOfflineMethod("bank_transfer"); }}
                      data-testid={`mark-paid-offline-${iv.id}`}
                      className="text-emerald-800 hover:bg-emerald-50"
                      title="Record bank transfer / cash / cheque payment"
                    >
                      <Bank size={15} className="mr-1" /> Bank transfer
                    </Button>
                  )}
                  {!readOnly && (
                    <Button variant="ghost" size="sm" onClick={() => remove(iv.id)} data-testid={`delete-inv-${iv.id}`} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash size={15} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {total > pageSize && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-200 text-xs text-zinc-600" data-testid="invoice-pagination">
            <div>
              Showing <b>{(page - 1) * pageSize + 1}</b>–<b>{Math.min(page * pageSize, total)}</b> of <b>{total}</b>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
                <CaretLeft size={14} />
              </Button>
              <div className="px-3 py-1.5 border border-zinc-200 rounded-md bg-white">{page} / {totalPages}</div>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>
                <CaretRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Offline payment dialog (bank transfer / cash / cheque) */}
      <Dialog open={!!offlineInv} onOpenChange={(o) => { if (!o) setOfflineInv(null); }}>
        <DialogContent className="max-w-md" data-testid="offline-pay-dialog">
          <DialogHeader>
            <DialogTitle>Record offline payment</DialogTitle>
          </DialogHeader>
          {offlineInv && (
            <div className="space-y-4">
              <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2.5 text-sm">
                <div className="flex items-baseline justify-between gap-3">
                  <code className="text-xs font-mono bg-white border border-zinc-200 rounded px-1.5 py-0.5">{offlineInv.invoice_number}</code>
                  <span className="font-semibold text-zinc-900">{offlineInv.currency || "JMD"} ${Number(offlineInv.amount).toLocaleString()}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1 truncate">{offlineInv.customer_name}</div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Payment method</Label>
                <Select value={offlineMethod} onValueChange={setOfflineMethod}>
                  <SelectTrigger className="mt-1.5" data-testid="offline-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Reference (optional)</Label>
                <Input
                  className="mt-1.5"
                  value={offlineRef}
                  onChange={(e) => setOfflineRef(e.target.value)}
                  placeholder="Bank slip #, wire confirmation, cheque #…"
                  data-testid="offline-reference"
                />
                <p className="text-[11px] text-zinc-500 mt-1">Used for reconciliation. Shown on audit log.</p>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Internal note (optional)</Label>
                <Textarea
                  rows={2}
                  className="mt-1.5"
                  value={offlineNote}
                  onChange={(e) => setOfflineNote(e.target.value)}
                  placeholder="Any notes for the audit trail…"
                  data-testid="offline-note"
                />
              </div>
              <details className="rounded-md border border-zinc-200 bg-white text-xs" data-testid="offline-pay-bank-block">
                <summary className="cursor-pointer px-3 py-2 font-semibold text-zinc-700">
                  Bank account details (Sagicor Bank, Jamaica)
                </summary>
                <div className="px-3 pb-3 text-zinc-600 space-y-1.5 font-mono text-[11px]">
                  <div>Beneficiary: <b>Cyrus Field Media Capital Ltd</b></div>
                  <div className="pt-1.5 border-t border-zinc-100">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">USD Account</div>
                    <div>5503986665 · Savings</div>
                  </div>
                  <div className="pt-1.5 border-t border-zinc-100">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">JMD Account</div>
                    <div>5503875828 · Savings</div>
                  </div>
                </div>
              </details>
              <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                Submitting will mark this invoice paid and (if applicable) automatically unlock the company's account.
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOfflineInv(null)} disabled={offlineSaving}>Cancel</Button>
            <Button
              onClick={submitOfflinePay}
              disabled={offlineSaving}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="offline-pay-confirm"
            >
              {offlineSaving ? "Saving…" : (<><Check size={14} className="mr-1" /> Mark paid</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}</Label>
      <Input type={type} className="mt-1.5" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5">
      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-500">{label}</div>
      <div className="text-2xl font-semibold mt-2" style={{ color, fontFamily: "Work Sans" }}>{value}</div>
    </div>
  );
}
